import * as THREE from "three/webgpu";
import * as TSL from "three/tsl";
import { ao } from "three/examples/jsm/tsl/display/GTAONode";
import { ssr } from "three/examples/jsm/tsl/display/SSRNode";
import { smaa } from "three/examples/jsm/tsl/display/SMAANode";
import { bloom } from "three/examples/jsm/tsl/display/BloomNode";
import { lensflare } from "three/addons/tsl/display/LensflareNode.js";
import { ssgi } from "three/addons/tsl/display/SSGINode.js";
import { traa } from "three/addons/tsl/display/TRAANode.js";

import { uniform } from "three/tsl";
import { useState, useLayoutEffect } from "react";
import { useThree, useFrame } from "@react-three/fiber";
import { useSnapshot } from "valtio";
import { state } from "./store";
import { gaussianBlur } from "three/examples/jsm/tsl/display/GaussianBlurNode.js";

export default function Effects() {
  const { qualitySettings } = useSnapshot(state);
  const { gl, scene, camera } = useThree();
  const [postProcessing] = useState(() => new THREE.PostProcessing(gl));
  // Configure passes
  useLayoutEffect(() => {
    const scenePass = TSL.pass(scene, camera, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      // generateMipmaps: false,
    });

    // setDpr([1, qualitySettings === "ultra" ? 2 : 1.5]);

    scenePass.setMRT(
      TSL.mrt({
        output: TSL.output,
        // diffuseColor: TSL.diffuseColor,
        normal: TSL.directionToColor(TSL.normalView),
        metalrough: TSL.vec2(TSL.metalness, TSL.roughness),
        velocity: TSL.velocity,
      })
    );
    const scenePassColor = scenePass.getTextureNode("output");
    // const scenePassDiffuse = scenePass.getTextureNode("diffuseColor");

    // console.log(scenePassDiffuse);
    const scenePassNormal = scenePass.getTextureNode("normal");
    const scenePassDepth = scenePass.getTextureNode("depth");
    const scenePassVelocity = scenePass.getTextureNode("velocity");
    const scenePassMetalrough = scenePass.getTextureNode("metalrough");

    const normalTexture = scenePass.getTexture("normal");
    if (normalTexture) {
      normalTexture.type = THREE.UnsignedByteType;
    }

    const sceneNormal = TSL.sample((uv) => TSL.colorToDirection(scenePassNormal.sample(uv)));

    const ssrPass = ssr(
      scenePassColor,
      scenePassDepth,
      sceneNormal,
      scenePassMetalrough.r,
      scenePassMetalrough.g
      // 0.25, // metalness
      // 0.25 // roughness
    );

    ssrPass.maxDistance.value = 3;
    ssrPass.quality.value = 0.5;
    ssrPass.blurQuality.value = qualitySettings === "ultra" ? 0.6 : 0.5;
    ssrPass.thickness.value = 0.5;
    ssrPass.resolutionScale = qualitySettings === "ultra" ? 0.9 : 0.9;

    // GTAO (Ground Truth Ambient Occlusion) Pass

    const aoPass = ao(scenePassDepth, sceneNormal, camera);
    aoPass.radius = 1;
    aoPass.scale = 0.8;
    aoPass.resolutionScale = 0.8;
    aoPass.distanceFallOff.value = 0.8;
    aoPass.thickness = 5;

    const bloomPass = bloom(scenePassColor, 0.05, 0.8, 0.9);

    // SSGI (Screen Space Global Illumination) - only for Ultra quality
    let finalColor = scenePassColor;
    let blendPassAO;

    if (qualitySettings === "ultra") {
      const ssgiPass = ssgi(scenePassColor, scenePassDepth, sceneNormal, camera);

      ssgiPass.sliceCount.value = 2;
      ssgiPass.stepCount.value = 6;
      ssgiPass.radius.value = 6;
      ssgiPass.giIntensity.value = 20.0;
      ssgiPass.aoIntensity.value = 1.5;
      ssgiPass.thickness.value = 1.5;
      ssgiPass.useTemporalFiltering = true;
      ssgiPass.resolutionScale = 0.5;
      ssgiPass.useScreenSpaceSampling.value = false;
      ssgiPass.useLinearThickness.value = false;
      ssgiPass.backfaceLighting.value = 0;

      const giBlurIntensity = uniform(0.25); // Default blur intensity
      const giBlurred = gaussianBlur(ssgiPass.rgb, giBlurIntensity, 24);
      const gi = giBlurred;

      // Extract GI and AO from SSGI (following example)
      const ao = gaussianBlur(ssgiPass.a, 0.25, 24);

      // Composite: sceneColor * AO + diffuseColor * GI (following example)
      // Break down complex expression to avoid parameter length issues
      const sceneColorWithGI = scenePassColor.rgb.mul(gi);
      const sceneColorWithAO = scenePassColor.rgb.mul(ssgiPass.a);
      const compositeRGB = TSL.add(sceneColorWithGI, sceneColorWithAO);
      // Resolve o problema do erro de THREE.TSL: Length of parameters exceeds maximum length of function 'vec4()' type.
      finalColor = TSL.vec4(compositeRGB.rgb, scenePassColor.a);

      // In ultra mode, SSGI already has AO, so skip aoPass
      blendPassAO = finalColor.add(bloomPass);
    } else {
      // In non-ultra modes, apply standard AO pass
      blendPassAO = aoPass.getTextureNode().r.mul(finalColor).add(bloomPass);
    }

    const threshold = uniform(0.15);
    const ghostAttenuationFactor = uniform(30);
    const ghostSpacing = uniform(0.25);

    const flarePass = lensflare(bloomPass, {
      threshold,
      ghostAttenuationFactor,
      ghostSpacing,
    });
    flarePass.scale = 0.5;
    flarePass.resolutionScale = 0.5;

    // Create composite pass
    const compositePass = TSL.blendColor(blendPassAO, ssrPass).add(flarePass);

    // Apply TRAA (Temporal Reprojection Anti-Aliasing) - following example
    const traaPass = traa(compositePass, scenePassDepth, scenePassVelocity, camera);

    postProcessing.outputNode = traaPass;
  }, [scene, camera, qualitySettings]);
  // Take over render queue
  postProcessing.needsUpdate = true;
  useFrame(() => postProcessing.render(), 1);
}
